import { WebSettingsDto, ApiResponse } from "@/components/types";
import axios, { AxiosInstance, AxiosResponse } from "axios";
import notification from "@/store/notification";

export default class DispatchClient {
  private client!: AxiosInstance;
  constructor(baseUrl: string, token: string, unionId: string) {
    this.init(baseUrl, token, unionId);
  }

  public init(baseUrl: string, token: string, unionId: string) {
    this.client = axios.create({
      baseURL: baseUrl,
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        "X-Union-Id": unionId,
      },
      validateStatus: () => true,
    });
  }

  public async get<T>(url: string): Promise<ApiResponse<T>> {
    return this.handleResponse(this.client.get<T>(url));
  }

  public async delete<T>(url: string): Promise<ApiResponse<T>> {
    return this.handleResponse(this.client.delete<T>(url));
  }

  public async post<T>(url: string, data: any): Promise<ApiResponse<T>> {
    return this.handleResponse(
      this.client.post<T>(url, data, {
        headers: {
          "Content-Type": "application/json",
        },
      })
    );
  }

  private async handleResponse<T>(
    promise: Promise<AxiosResponse<T>>
  ): Promise<ApiResponse<T>> {
    try {
      const resp = await promise;
      return this.toApiResponse(resp);
    } catch (error) {
      notification.show("An error ocurred", "error");
      console.error("Error:", error);
      throw error;
    }
  }

  private toApiResponse<T>(resp: AxiosResponse<T>): ApiResponse<T> {
    if (resp.status >= 500) {
      throw new Error(resp.statusText);
    } else if (resp.status >= 400) {
      const msg: string =
        (resp.data as any)?.message || resp.data || resp.statusText;
      throw new Error(msg);
    }

    return {
      status: resp.status,
      message: resp.statusText,
      response: resp.data,
      success: resp.status < 400,
    };
  }
}

export const client = new DispatchClient(
  "https://duat.api.pbasconnect.com/dispatch",
  "eyJhbGciOiJSUzI1NiIsImtpZCI6ImNmZjA0MTVhMzk2NzFlMzc1NjgyZjBmOTMyZjA1ODcyIiwidHlwIjoiSldUIn0.eyJuYmYiOjE3MjU1NTEwNTcsImV4cCI6MTcyNTU1NDY1NywiaXNzIjoiaHR0cDovL2xvY2FsaG9zdDo1MDAxIiwiYXVkIjpbImh0dHA6Ly9sb2NhbGhvc3Q6NTAwMS9yZXNvdXJjZXMiLCJwbGFucyJdLCJjbGllbnRfaWQiOiJmb28iLCJzdWIiOiJkNzE4ODUwZS05ZWFjLTQzZjItYjIzOS0yNDI4OTBhZjFmODkiLCJhdXRoX3RpbWUiOjE3MjU1NTEwNTcsImlkcCI6ImxvY2FsIiwicHJlZmVycmVkX3VzZXJuYW1lIjoiU3VwZXJBZG1pbiIsImVtYWlsIjoic3VwZXIuYWRtaW5AcGJhcy5jYSIsImNvbmZpcm1lZCI6IlRydWUiLCJicm9rZXJpZCI6IjgzODdkYTQ3LWVjNWQtNDRmNS1hYTYzLWMyOTlhOWM2MzM5ZiIsImxhbmd1YWdlIjoiZW4iLCJmaXJzdE5hbWUiOiJTdXBlciIsImxhc3ROYW1lIjoiQWRtaW4iLCJwbGFucyI6IltcInRlc3RfcGxhblwiLFwiMDA0XCJdIiwidW5pb25zIjoiW1wiMGZkNmNlZWEtOWUzYy00OWViLWEwMzItN2I5ZjEyNGYzMDY3XCJdIiwiZW1wbG95ZXJzIjoiW10iLCJzdG9yZXMiOiJbXSIsInJvbGUiOlsiQ2xhaW1zIEFkbWluaXN0cmF0b3IiLCJEb2N1bWVudHMgTWFuYWdlbWVudCBBZG1pbiIsIk1lbWJlciBQb3J0YWwgQWRtaW4iLCJFbXBsb3llciBQcmVtaXVtcyBWaWV3ZXIiLCJQcm92aWRlciIsIkJyb2tlciBQbGFuIE1hbmFnZXIiLCJCcm9rZXIgUGxhbiBDcmVhdG9yIiwiRW1wbG95ZXIgQmVuZWZpY2lhcmllcyBBZG1pbmlzdHJhdG9yIiwiRW1wbG95ZXIgTWVtYmVycyBWaWV3ZXIiLCJCcm9rZXIgU3VwcG9ydCBBZG1pbiIsIkVtcGxveWVyIEJpbGxpbmcgVmlld2VyIiwiVW5pb24gQWRtaW4iLCJNZW1iZXIgUG9ydGFsIFVzZXJzIEFkbWluIiwiRW1wbG95ZXIgRGlyZWN0IERlcG9zaXQgQWRtaW5pc3RyYXRvciIsIkJyb2tlciBVbmlvbiBBZG1pbiIsIkVtcGxveWVyIFVuaW9uIEFkbWluIiwiQnJva2VyIFBsYW4gQWRtaW4iLCJHbG9iYWwgU3VwcG9ydCBNYW5hZ2VyIl0sImh0dHA6Ly9zY2hlbWFzLm1pY3Jvc29mdC5jb20vd3MvMjAwOC8wNi9pZGVudGl0eS9jbGFpbXMvcm9sZSI6WyJDbGFpbXMgQWRtaW5pc3RyYXRvciIsIkRvY3VtZW50cyBNYW5hZ2VtZW50IEFkbWluIiwiTWVtYmVyIFBvcnRhbCBBZG1pbiIsIkVtcGxveWVyIFByZW1pdW1zIFZpZXdlciIsIlByb3ZpZGVyIiwiQnJva2VyIFBsYW4gTWFuYWdlciIsIkJyb2tlciBQbGFuIENyZWF0b3IiLCJFbXBsb3llciBCZW5lZmljaWFyaWVzIEFkbWluaXN0cmF0b3IiLCJFbXBsb3llciBNZW1iZXJzIFZpZXdlciIsIkJyb2tlciBTdXBwb3J0IEFkbWluIiwiRW1wbG95ZXIgQmlsbGluZyBWaWV3ZXIiLCJVbmlvbiBBZG1pbiIsIk1lbWJlciBQb3J0YWwgVXNlcnMgQWRtaW4iLCJFbXBsb3llciBEaXJlY3QgRGVwb3NpdCBBZG1pbmlzdHJhdG9yIiwiQnJva2VyIFVuaW9uIEFkbWluIiwiRW1wbG95ZXIgVW5pb24gQWRtaW4iLCJCcm9rZXIgUGxhbiBBZG1pbiIsIkdsb2JhbCBTdXBwb3J0IE1hbmFnZXIiXSwiZWRpdG9yIjpbInNydi11c2VyLW1hbmFnZW1lbnQtZG90bmV0LnVzZXIiLCJzcnYtdXNlci1tYW5hZ2VtZW50LWRvdG5ldC5icm9rZXIiXSwibWFuYWdlciI6WyJzcnYtdXNlci1tYW5hZ2VtZW50LWRvdG5ldC51c2VyIiwic3J2LXVzZXItbWFuYWdlbWVudC1kb3RuZXQuYnJva2VyIl0sInZpZXdlciI6WyJzcnYtdXNlci1tYW5hZ2VtZW50LWRvdG5ldC51c2VyIiwic3J2LXVzZXItbWFuYWdlbWVudC1kb3RuZXQuYnJva2VyIl0sInNjb3BlIjpbIm9wZW5pZCIsInByb2ZpbGUiLCJwbGFucyJdLCJhbXIiOlsicHdkIl19.jIZbMkE09BJTJzm79MmByzriWWOLMoz-5046XovGe91k0jCXvYRXFBUbDkp_XvQj5jRMxMJkvwfItO-4Uu6Fd3a_s26e66zqwcLU2RRc11fmy0fVMoc2m2x6_W1-dmwgfhptGn-Uxjp57H1xlE9as1x40Klo5f0QlTQNKcM_x0mFBQbQocD9ArUMWDjACKgjPNo5IWB7BZjveu-2AjmYDxe6IASN3hBaJwxa34yqfPIC1rmW3oKBvAXYzjVFI8Qm2rQMlrXZhCym5TKmnTEDu8-33jWGL-VLIdwJaCpN-bAPsV9gsJ3FMtTyze9c7Jvgi-3jUiReLKz2c64XFMNpFg",
  "0fd6ceea-9e3c-49eb-a032-7b9f124f3067"
);
